import React, { useEffect, useMemo, useState } from "react";
import '../../styles/StreamingPlatforms.css';
import { useNavigate, useParams } from "react-router-dom";
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { auth, db } from '../../config/firebase';
import { doc, getDoc } from "firebase/firestore";
import Movie from "./movie";
import TvShow from "./tvShow";
import { Hearts } from 'react-loader-spinner';

const providerIds = {
    "Disney Plus": 337,
    "Netflix": 8,
    "Amazon Prime Video": 9,
    "Apple TV Plus": 350,
};

const platforms = [
    {
        title: "Netflix",
        image: "netflix.svg",
        link: "https://www.netflix.com",
    },
    {
        title: "Amazon Prime Video",
        image: "amazon-prime.svg",
        link: "https://www.amazon.com/prime",
    },
    {
        title: "Disney Plus",
        image: "disney-plus.svg",
        link: "https://www.disneyplus.com",
    },
    {
        title: "Apple TV Plus",
        image: "apple-tv.svg",
        link: "https://www.apple.com/apple-tv-plus",
    },
];

const movieGenres = {
    "Action": 28,
    "Adventure": 12,
    "Animation": 16,
    "Comedy": 35,
    "Crime": 80,
    "Documentary": 99,
    "Drama": 18,
    "Family": 10751,
    "Fantasy": 14,
    "History": 36,
    "Horror": 27,
    "Music": 10402,
    "Mystery": 9648,
    "Romance": 10749,
    "TV Movie": 10770,
    "Thriller": 53,
    "War": 10752,
    "Western": 37
};

const tvShowGenres = {
    "Action & Adventure": 10759,
    "Animation": 16,
    "Comedy": 35,
    "Crime": 80,
    "Documentary": 99,
    "Drama": 18,
    "Family": 10751,
    "Kids": 10762,
    "Mystery": 9648,
    "News": 10763,
    "Reality": 10764,
    "Sci-Fi & Fantasy": 10765,
    "Soap": 10766,
    "Talk": 10767,
    "War & Politics": 10768,
    "Western": 37
};

const StreamingService = () => {
    const { platformName } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [content, setContent] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isIntersectionContent, setIsIntersectionContent] = useState(false);
    const [partnerRegion, setPartnerRegion] = useState('');
    const [userRegion, setUserRegion] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [contentCounts, setContentCounts] = useState({
        region1Count: 0,
        region2Count: 0,
        sharedCount: 0
    });

    // Helper functions for sessionStorage cache
    const getCacheKey = (platformTitle, tabIndex) => `streaming_cache_${platformTitle}_${tabIndex}`;
    
    const getFromCache = (platformTitle, tabIndex) => {
        try {
            const cached = sessionStorage.getItem(getCacheKey(platformTitle, tabIndex));
            return cached ? JSON.parse(cached) : null;
        } catch (e) {
            console.warn('Error reading from cache:', e);
            return null;
        }
    };
    
    const saveToSessionCache = (platformTitle, tabIndex, data) => {
        try {
            sessionStorage.setItem(getCacheKey(platformTitle, tabIndex), JSON.stringify(data));
        } catch (e) {
            console.warn('Error saving to cache:', e);
        }
    };

    // Find platform based on URL parameter
    const platform = platforms.find(p =>
        p.title.toLowerCase().replace(/\s+/g, '-') === platformName
    );

    const [filters, setFilters] = useState({
        name: '',
        genre: '',
        year: '',
        rating: ''
    });
    // Staged filters for the "Apply Filters" UX
    const [pendingFilters, setPendingFilters] = useState({
        name: '',
        genre: '',
        year: '',
        rating: ''
    });

    const onBack = () => {
        navigate('/dashboard/dates/movies');
    };

    const fetchContent = async (contentType, isLoadMore = false) => {
        console.log('fetchContent called with contentType:', contentType, 'isLoadMore:', isLoadMore);
        try {
            if (isLoadMore) {
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
                setCurrentPage(1);
                setHasMore(true);
            }
            
            const providerId = providerIds[platform.title];
            const contentTypeParam = contentType === 0 ? 'movies' : 'tv';
            console.log('Fetching content:', { providerId, contentTypeParam, contentType });
            const startPage = isLoadMore ? currentPage : 1;
            const maxPages = 4; // Fetch ~70-80 items (4 pages * ~20 items per page)

            // Get current user
            const currentUser = auth.currentUser;
            if (!currentUser) {
                console.error('No authenticated user found');
                setIsLoading(false);
                setIsLoadingMore(false);
                return;
            }

            // Get user document
            const userDocRef = doc(db, "UserInformation", currentUser.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                console.error('User document not found');
                setIsLoading(false);
                setIsLoadingMore(false);
                return;
            }

            const userData = userDoc.data();

            // Get country code for current user
            const userCountryCode = userData.countryCode || 'US';
            if (!isLoadMore) {
                setUserRegion(userCountryCode);
            }

            // Check if user is in a relationship
            if (userData.inRelation === true && userData.inRelationWith) {
                // Get partner's document
                const partnerDocRef = doc(db, "UserInformation", userData.inRelationWith);
                const partnerDoc = await getDoc(partnerDocRef);

                if (!partnerDoc.exists()) {
                    console.error('Partner document not found');
                    setIsIntersectionContent(false);
                    // Fall back to just the user's country code
                    const endpoint = contentType === 0 ? 'fetchMovies' : 'fetchTVShows';
                    const response = await fetch(`https://api.frazza.online/${endpoint}?platform=${providerId}&watch_region=${userCountryCode}&startPage=${startPage}&maxPages=${maxPages}`);
                    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                    const data = await response.json();
                    
                    if (isLoadMore) {
                        setContent(prev => [...prev, ...data]);
                    } else {
                        setContent(data);
                    }
                    setCurrentPage(startPage + maxPages);
                    setHasMore(data.length >= 40);
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    return;
                }

                const partnerData = partnerDoc.data();
                const partnerCountryCode = partnerData.countryCode || 'US';
                if (!isLoadMore) {
                    setPartnerRegion(partnerCountryCode);
                    setIsIntersectionContent(true);
                }

                // Use the intersection endpoint
                const response = await fetch(
                    `https://api.frazza.online/fetchIntersectionContent?platform=${providerId}&contentType=${contentTypeParam}&region1=${userCountryCode}&region2=${partnerCountryCode}&startPage=${startPage}&maxPages=${maxPages}`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();

                const data = result.content || [];
                const counts = result.counts || {
                    region1Count: 0,
                    region2Count: 0,
                    sharedCount: 0
                };

                if (isLoadMore) {
                    setContent(prev => [...prev, ...data]);
                } else {
                    setContent(data);
                }
                setCurrentPage(startPage + maxPages);
                setContentCounts(counts);
                setHasMore(data.length >= 40);

            } else {
                // User is not in a relationship, fetch content just for their region
                if (!isLoadMore) {
                    setIsIntersectionContent(false);
                    setPartnerRegion('');
                }

                const endpoint = contentType === 0 ? 'fetchMovies' : 'fetchTVShows';
                const response = await fetch(`https://api.frazza.online/${endpoint}?platform=${providerId}&watch_region=${userCountryCode}&startPage=${startPage}&maxPages=${maxPages}`);

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                
                if (isLoadMore) {
                    setContent(prev => [...prev, ...data]);
                } else {
                    setContent(data);
                }
                setCurrentPage(startPage + maxPages);
                setHasMore(data.length >= 40);
            }
        } catch (error) {
            console.error('Error fetching content:', error);
            setHasMore(false);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    // Save to sessionStorage cache whenever content or related state changes
    useEffect(() => {
        if (platform && content.length > 0 && !isLoading && !isLoadingMore) {
            const cacheKey = getCacheKey(platform.title, activeIndex);
            console.log('Saving to cache:', cacheKey, 'content items:', content.length, 'first item:', content[0]?.title || content[0]?.name);
            saveToSessionCache(platform.title, activeIndex, {
                content,
                currentPage,
                hasMore,
                userRegion,
                partnerRegion,
                isIntersectionContent,
                contentCounts,
                timestamp: Date.now()
            });
        }
    }, [content, currentPage, hasMore, isLoading, isLoadingMore, platform, activeIndex]);

    const loadMore = () => {
        fetchContent(activeIndex, true);
    };

    useEffect(() => {
        if (platform) {
            console.log('Tab switching - activeIndex:', activeIndex, 'platform:', platform.title);
            // Check if we have cached content for this platform/tab combination
            const cached = getFromCache(platform.title, activeIndex);
            console.log('Cache check result:', cached ? 'found' : 'not found');
            
            if (cached) {
                // Restore from cache instead of fetching
                console.log('Restoring from cache, content length:', cached.content.length);
                setIsLoading(false);
                setContent(cached.content);
                setCurrentPage(cached.currentPage);
                setHasMore(cached.hasMore);
                setUserRegion(cached.userRegion);
                setPartnerRegion(cached.partnerRegion);
                setIsIntersectionContent(cached.isIntersectionContent);
                setContentCounts(cached.contentCounts);
            } else {
                // No cache, fetch new data
                console.log('No cache found, fetching content for activeIndex:', activeIndex);
                setIsLoading(true);
                setContent([]);
                setCurrentPage(1);
                setHasMore(true);
                fetchContent(activeIndex);
            }
        }
    }, [platform, activeIndex]);

    const handleTabChange = (e) => {
        console.log('Tab changing from', activeIndex, 'to', e.index);
        setActiveIndex(e.index);
        setShowFilters(false);
        // Clear content immediately to avoid showing wrong type
        setContent([]);
        setIsLoading(true);
        // Clear filters when switching tabs
        setFilters({ name: '', genre: '', year: '', rating: '' });
        setPendingFilters({ name: '', genre: '', year: '', rating: '' });
    };

    const handlePendingChange = (name, value) => {
        setPendingFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setFilters(pendingFilters);
    };

    const clearFilters = () => {
        setFilters({ name: '', genre: '', year: '', rating: '' });
        setPendingFilters({ name: '', genre: '', year: '', rating: '' });
    };

    const getGenreOptions = () => {
        const genres = activeIndex === 0 ? movieGenres : tvShowGenres;
        return [{ label: 'All Genres', value: '' }, ...Object.keys(genres).map(genre => ({
            label: genre,
            value: genre
        }))];
    };

    const activeFiltersCount = Object.values(filters).filter(Boolean).length;

    const filteredContent = content.filter(item => {
        const title = item.title || item.name || '';
        const genreIds = activeIndex === 0 ? movieGenres : tvShowGenres;
        const matchesName = title.toLowerCase().includes((filters.name || '').toLowerCase());
        const matchesGenre = filters.genre ? item.genre_ids.includes(genreIds[filters.genre]) : true;
        const matchesYear = filters.year ? (item.release_date || item.first_air_date || '').split('-')[0] === filters.year : true;
        const matchesRating = filters.rating ? Math.round(item.vote_average / 2) >= parseInt(filters.rating) : true;
        return matchesName && matchesGenre && matchesYear && matchesRating;
    });

    const matchCount = filteredContent.length;

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const years = [];
        for (let y = current; y >= 1960; y--) years.push({ label: String(y), value: String(y) });
        return [{ label: 'Any Year', value: '' }, ...years];
    }, []);

    const renderFilters = () => (
        <div className="filter-section-compact">
            {/* Search bar with filter toggle */}
            <div className="filter-bar">
                <div className="search-wrapper">
                    <i className="pi pi-search"></i>
                    <InputText
                        value={pendingFilters.name}
                        onChange={(e) => {
                            handlePendingChange('name', e.target.value);
                            setFilters(prev => ({ ...prev, name: e.target.value }));
                        }}
                        placeholder={`Search ${activeIndex === 0 ? 'movies' : 'TV shows'}...`}
                        className="search-input-compact"
                    />
                </div>
                <button 
                    className={`filter-toggle-btn ${showFilters ? 'active' : ''} ${activeFiltersCount > 0 ? 'has-filters' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <i className="pi pi-sliders-h"></i>
                    {activeFiltersCount > 0 && <span className="filter-badge">{activeFiltersCount}</span>}
                </button>
                {activeFiltersCount > 0 && (
                    <button className="clear-filters-btn" onClick={clearFilters}>
                        <i className="pi pi-times"></i>
                    </button>
                )}
            </div>
            
            {/* Expandable filter options */}
            {showFilters && (
                <div className="filter-options-row">
                    <div className="filter-chip-group">
                        <Dropdown
                            value={pendingFilters.genre}
                            options={getGenreOptions()}
                            onChange={(e) => {
                                handlePendingChange('genre', e.value);
                                setFilters(prev => ({ ...prev, genre: e.value }));
                            }}
                            placeholder="Genre"
                            className="filter-dropdown-compact"
                        />
                        <Dropdown
                            value={pendingFilters.year}
                            options={yearOptions}
                            onChange={(e) => {
                                handlePendingChange('year', e.value);
                                setFilters(prev => ({ ...prev, year: e.value }));
                            }}
                            placeholder="Year"
                            className="filter-dropdown-compact"
                        />
                        <div className="rating-filter-compact">
                            {[1,2,3,4,5].map(rating => (
                                <button
                                    key={rating}
                                    type="button"
                                    className={`star-compact ${parseInt(pendingFilters.rating || '0') >= rating ? 'active' : ''}`}
                                    onClick={() => {
                                        const newRating = pendingFilters.rating === String(rating) ? '' : String(rating);
                                        handlePendingChange('rating', newRating);
                                        setFilters(prev => ({ ...prev, rating: newRating }));
                                    }}
                                >
                                    <i className={parseInt(pendingFilters.rating || '0') >= rating ? 'pi pi-star-fill' : 'pi pi-star'} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Results count */}
            <div className="results-count">
                <span>{matchCount} {activeIndex === 0 ? 'movies' : 'shows'}</span>
                {activeFiltersCount > 0 && <span className="filtered-label">filtered</span>}
            </div>
        </div>
    );

    return (
        <div className="streaming-service-container">
            <div className="streaming-service-header" >
                <Button
                    icon="pi pi-arrow-left"
                    color="white"
                    onClick={onBack}
                />
                <h1 className="platform-title">{platform?.title || 'Loading...'}</h1>
            </div>
            <TabView
                className="custom-tabs"
                activeIndex={activeIndex}
                onTabChange={handleTabChange}
                pt={{
                    root: { style: { width: '100%' } },
                    nav: { style: { display: 'flex', width: '100%' } }
                }}
            >
                <TabPanel header="Movies">
                    {renderFilters()}
                    <div className="content-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '1rem'
                    }}>
                        {isLoading ? (
                            <div className="loading-container" style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '2rem'
                            }}>
                                <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                                <p>Loading movies...</p>
                            </div>
                        ) : filteredContent.length > 0 ? (
                            <>
                                {filteredContent.map((item, index) => (
                                    <Card key={index} className="movie-card">
                                        <Movie
                                            title={item.title}
                                            year={item.release_date ? item.release_date.split('-')[0] : 'N/A'}
                                            image={`https://image.tmdb.org/t/p/w500${item.poster_path}`}
                                            rating={Math.round(item.vote_average / 2)}
                                            id={item.id}
                                            type="movie"
                                        />
                                    </Card>
                                ))}
                                {hasMore && (
                                    <Card className="movie-card" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '270px',
                                        cursor: 'pointer',
                                        background: '#f97316',
                                        border: 'none'
                                    }} onClick={loadMore}>
                                        <div style={{
                                            textAlign: 'center',
                                            color: 'white'
                                        }}>
                                            {isLoadingMore ? (
                                                <>
                                                    <Hearts height="50" width="50" color="white" ariaLabel="hearts-loading" visible={true} />
                                                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>Loading...</p>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="pi pi-plus-circle" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                                                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>See More</p>
                                                </>
                                            )}
                                        </div>
                                    </Card>
                                )}
                            </>
                        ) : (
                            <div className="no-content" style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '2rem'
                            }}>
                                <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                                <p>No movies found with the current filters.</p>
                            </div>
                        )}
                    </div>
                </TabPanel>

                <TabPanel header="TV Shows">
                    {renderFilters()}

                    <div className="content-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '1rem'
                    }}>
                        {isLoading ? (
                            <div className="loading-container" style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '2rem'
                            }}>
                                <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
                                <p>Loading TV shows...</p>
                            </div>
                        ) : filteredContent.length > 0 ? (
                            <>
                                {filteredContent.map((item, index) => (
                                    <Card key={index} className="show-card">
                                        <TvShow
                                            title={item.name}
                                            year={item.first_air_date ? item.first_air_date.split('-')[0] : 'N/A'}
                                            image={`https://image.tmdb.org/t/p/w500${item.poster_path}`}
                                            rating={Math.round(item.vote_average / 2)}
                                            id={item.id}
                                            type="tv"
                                        />
                                    </Card>
                                ))}
                                {hasMore && (
                                    <Card className="show-card" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '270px',
                                        cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none'
                                    }} onClick={loadMore}>
                                        <div style={{
                                            textAlign: 'center',
                                            color: 'white'
                                        }}>
                                            {isLoadingMore ? (
                                                <>
                                                    <Hearts height="50" width="50" color="white" ariaLabel="hearts-loading" visible={true} />
                                                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>Loading...</p>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="pi pi-plus-circle" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                                                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>See More</p>
                                                </>
                                            )}
                                        </div>
                                    </Card>
                                )}
                            </>
                        ) : (
                            <div className="no-content" style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '2rem'
                            }}>
                                <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                                <p>No TV shows found with the current filters.</p>
                            </div>
                        )}
                    </div>
                </TabPanel>
            </TabView>
        </div>
    );
};

export default StreamingService;
